import {isElectron} from "react-device-detect";
import {getLocaleId} from "./locale-selector";
import configApp from "../config/config";
import {saveDevices, saveEmployeeProps, saveStructure} from "./lamp";
import lampManage from "./lamp-manage";

const initialState = {
    initialized: false,
    errors: null,
    notices: null,
    theme_name: 'default_theme',
    document_title: 'Lamp',
    logo: `${configApp.app_dir}assets/img/logo.png`,
    logo_negative: `${configApp.app_dir}assets/img/logo.png`,
    favicon: `${configApp.app_dir}favicon.ico`,
    modules: [],
    settings: {
        server_host: '',
        fullscreen: false,
        cursor: false,
        window_name: '',
    }
}

const INITIALIZED_SUCCESS = 'INITIALIZED_SUCCESS';
const DOCUMENT_TITLE = 'DOCUMENT_TITLE';
const FATAL_ERROR = 'FATAL_ERROR';
const NOTICE_ERROR = 'NOTICE_ERROR';
const DEFAULT_STATE_APP = 'DEFAULT_STATE_APP';
const SET_THEME = 'SET_THEME';
const SET_LOGO = 'SET_LOGO';
const SET_LOGO_NEGATIVE = 'SET_LOGO_NEGATIVE';
const SET_FAVICON = 'SET_FAVICON';
const SET_MODULES = 'SET_MODULES';
const CLEAR_ERRORS = 'CLEAR_ERRORS';
const SAVE_SETTINGS = 'SAVE_SETTINGS';

export default function appReducer(state = initialState, action) {
    switch (action.type) {
        case INITIALIZED_SUCCESS:
            return {
                ...state,
                initialized: true
            };
        case NOTICE_ERROR:
            return {
                ...state,
                notices: action.errors
            };
        case FATAL_ERROR:
            return {
                ...state,
                initialized: true,
                errors: action.errors
            };
        case CLEAR_ERRORS:
            return {
                ...state,
                errors: null
            };
        case SET_THEME:
            return {
                ...state,
                theme_name: action.theme_name,
            };
        case SET_LOGO:
            return {
                ...state,
                logo: action.logo,
            };
        case SET_FAVICON:
            return {
                ...state,
                favicon: action.favicon,
            };
        case SET_MODULES:
            return {
                ...state,
                modules: action.modules,
            }
        case SET_LOGO_NEGATIVE:
            return {
                ...state,
                logo_negative: action.logo_negative,
            };
        case DOCUMENT_TITLE:
            return {
                ...state,
                document_title: action.document_title
            };
        case DEFAULT_STATE_APP:
            return {
                ...initialState,
                theme_name: state.theme_name,
                logo: state.logo,
                logo_negative: state.logo_negative,
                document_title: state.document_title,
                favicon: state.favicon,
                settings: state.settings,
            };
        case SAVE_SETTINGS:
            return {
                ...state,
                settings: action.settings
            }
        default:
            return state;
    }
}

const initializedSuccess = () => ({type: INITIALIZED_SUCCESS});
const fatalError = (e) => ({type: FATAL_ERROR, errors: e});
const noticeError = (e) => ({type: NOTICE_ERROR, errors: e});
const typeSetDocumentTitle = (document_title) => ({type: DOCUMENT_TITLE, document_title: document_title});
const typeSetTheme = (theme_name) => ({type: SET_THEME, theme_name: theme_name});
const typeSetLogo = (logo) => ({type: SET_LOGO, logo: logo});
const typeSetLogoNegative = (logo_negative) => ({type: SET_LOGO_NEGATIVE, logo_negative: logo_negative});
const typeSetFavicon = (favicon) => ({type: SET_FAVICON, favicon: favicon});
const typeSetModules = (modules) => ({type: SET_MODULES, modules: modules});
const typeClearErrors = () => ({type: CLEAR_ERRORS});
const defaultStateApp = () => ({type: DEFAULT_STATE_APP});
const _saveSettings = (settings) => ({type: SAVE_SETTINGS, settings: settings});

const prepareSettings = (isRerender = true) => new Promise((resolve, reject) => {
    const apiApp = require("../api/apiApp").default;
    const storage = require('../localStorage');
    const store = storage.default;
    const state = store.getState();
    if (window.location.hash !== '#/settings') {
        let window_name = '';
        let window = state.appState.settings.window_name;
        if (isElectron) {
            window_name = configApp.window_name;
        } else {
            window_name = (window === '') ? configApp.window_name : window;
        };

        apiApp.getSettings(window_name)
        .then(response => {
            let time_segments = response.data.time_segments ? response.data.time_segments : [];
            if (isElectron) {
                try {
                    const electron = require('electron');
                    electron.ipcRenderer.send('save_server_settings', {...response.data});
                } catch (e) {
                    console.error(e.message);
                }
            };

            if (isRerender) {
                require('./locale').setLanguages(response.data.languages);
                setThemeCode(store.dispatch, response.data.theme_name);
                setLogo(store.dispatch, response.data.logo);
                setLogoNegative(store.dispatch, response.data.logo_negative);
                setFavicon(store.dispatch, response.data.favicon);
                setModules(store.dispatch, response.data.modules);
                setDocumentTitle(store.dispatch, response.data.window_title);
                lampManage.setIntervals(time_segments);
            };

            saveEmployeeProps(store.dispatch, response.data.employee_properties);
            saveStructure(store.dispatch, response.data.structures);
            saveDevices(store.dispatch, response.data.devices);
            resolve(resolve);
        }).catch(e => {
            errorApp(store.dispatch, e);
            reject(e);
        });
    } else {
        resolve(resolve);
    }
});

export const errorApp = (dispatch, e) => {
    if (isElectron) {
        try {
            const logger = require('electron-log');
            logger.error({error: e});
        } catch (e) {
            console.error(e)
        }
    }
    dispatch(fatalError(e));
}

export const noticeApp = (dispatch, e) => {
    if (isElectron) {
        try {
            const logger = require('electron-log');
            logger.error({error: e});
        } catch (e) {
            console.log(e)
        }
    }
    dispatch(noticeError(e));
}

export const initAppSuccess = (dispatch) => new Promise((resolve, reject) => {
    dispatch(initializedSuccess());
    resolve();
});

export const initializeApp = () => async (dispatch) => {
    let promise = prepareSettings(true)
    .then(() => {
        const store = require('../localStorage').loadState();
        let htmlNode = document.querySelector('html');
        let faviconNode = document.getElementById('favicon');
        htmlNode.lang = getLocaleId(store);
        faviconNode.href = require('./app-selector').getFavicon(store);

        Promise.all([promise])
        .then(() => initAppSuccess(dispatch))
        .catch(e => errorApp(dispatch, e))
        .finally(() => setInterval(() => prepareSettings(false).catch((e) => errorApp(dispatch, e)), 1800000));
    });
    return promise;
}

export const setDefaultStateApp = (dispatch) => dispatch(defaultStateApp());
export const setThemeCode = (dispatch, theme) => dispatch(typeSetTheme(theme));
export const setDocumentTitle = (dispatch, document_title) => dispatch(typeSetDocumentTitle(document_title));
export const setLogo = (dispatch, logo) => dispatch(typeSetLogo(logo));
export const setLogoNegative = (dispatch, logo) => dispatch(typeSetLogoNegative(logo));
export const setFavicon = (dispatch, favicon) => dispatch(typeSetFavicon(favicon));
export const setModules = (dispatch, modules) => dispatch(typeSetModules(modules));
export const clearErrors = (dispatch) => dispatch(typeClearErrors());
export const saveSettings = (dispatch, settings) => dispatch(_saveSettings(settings));